import { effect, inject, Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';

import { CookieConsentService } from '../privacy/cookie-consent.service';
import { environment } from '../../../environments/environment';

@Injectable({ providedIn: 'root' })
export class AnalyticsService {
  private readonly consentService = inject(CookieConsentService);
  private readonly router = inject(Router);
  private readonly connectionString = this.resolveConnectionString();

  private readonly appInsights = new ApplicationInsights({
    config: {
      connectionString: this.connectionString,
      disableExceptionTracking: false,
      enableAutoRouteTracking: false,
      disableFetchTracking: false,
      disableAjaxTracking: false,
    },
  });

  private initialized = false;

  constructor() {
    effect(() => {
      const consent = this.consentService.consent();
      if (consent?.analyticsEnabled) {
        this.initializeOnce();
      }
    });
  }

  initialize(): void {
    if (this.consentService.consent()?.analyticsEnabled) {
      this.initializeOnce();
    }
  }

  private initializeOnce(): void {
    if (this.initialized || !this.connectionString) {
      return;
    }

    this.appInsights.loadAppInsights();
    this.initialized = true;

    this.trackPageView(this.router.url);

    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event) => {
        const navigationEvent = event as NavigationEnd;
        this.trackPageView(navigationEvent.urlAfterRedirects);
      });
  }

  private trackPageView(url: string): void {
    if (!this.initialized) {
      return;
    }

    this.appInsights.trackPageView({
      name: url,
      uri: url,
    });
  }

  private resolveConnectionString(): string {
    const configuredValue = environment.appInsightsConnectionString?.trim() ?? '';

    // The token is replaced in CD before SWA deploy.
    if (!configuredValue || configuredValue === '__APPINSIGHTS_CONNECTION_STRING__') {
      return '';
    }

    return configuredValue;
  }
}
